import {
  GatewayControleQuinquennal,
  GatewayTicketControleQuinquennal,
} from "@conformite/gateway";
import { isAxiosError } from "axios";
import classNames from "classnames";
import { format } from "date-fns";
import {
  Divider,
  InfoCard,
  InfoTooltip,
  Link,
  LoadingPage,
  TagWithIcon,
  TitleWithNumber,
  useDialog,
  Button,
  Card,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import CheckIcon from "@endya-conformite/ui-kit/src/assets/icons/check-circle-outline.svg?react";
import Download from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import IaSparkleIcon from "@endya-conformite/ui-kit/src/assets/icons/ia-sparkle.svg?react";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { ConnectedPageTemplate } from "@src/components/templates/ConnectedPageTemplate";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { useAnalyseEstimationQuery } from "@src/data/controle-quinquennal/hook/useAnalyseEstimationQuery";
import {
  ANALYSE_COST_BLOCKED_MESSAGE,
  getAnalyseCostBlockedMessage,
  isAnalyseCostBlockedError,
} from "@src/helper/analyseEstimation.helper";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useDossierZipExport } from "@src/data/files/hook/useDossierZipExport";
import { useLaunchAnalyseMutation } from "@src/data/controle-quinquennal/hook/useLaunchAnalyseMutation";
import { useValidateCQMutation } from "@src/data/controle-quinquennal/hook/useValidateCQMutation";
import { isRecette } from "@src/helper/environnement.helper";
import {
  AXE_STATUS_MAP,
  getControleQuinquennalDateButoir,
  isControleQuinquennalReadOnly,
  TICKET_STATUS_MAP,
} from "@src/helper/ticket.helper";
import { formatTitle } from "@src/helper/title.helper";
import {
  axeTypeToLabel,
  controleTypeOrder,
  cqRapportUrl,
} from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import { controleQuinquennalKeys } from "@src/data/controle-quinquennal/hook/controle-quinquennal.keys";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCQMetriquesQuery } from "@src/data/controle-quinquennal/hook/useCQMetriquesQuery";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import { AnalyseEstimationMessage } from "./components/AnalyseEstimationMessage";
import style from "./CQAxesPage.module.scss";

const AI_PROCESSING_STATUS_LABELS: Record<
  GatewayControleQuinquennal.VerificationStatus,
  string
> = {
  [GatewayControleQuinquennal.VerificationStatus.PENDING]: "Analyse en cours…",
  [GatewayControleQuinquennal.VerificationStatus.SUCCESS]: "Analyse terminée",
  [GatewayControleQuinquennal.VerificationStatus.FAILED]: "Analyse échouée",
};

type ControleQuinquennalItemProps = {
  axe: GatewayControleQuinquennal.ControleQuinquennalAxeWithNumberOfFiles;
  idEntreprise?: string;
};
function ControleQuinquennalAxeItem({
  axe,
  idEntreprise,
}: ControleQuinquennalItemProps) {
  const info = AXE_STATUS_MAP[axe.status];

  return (
    <Link
      to={`/controle-quinquennal/${idEntreprise}/${axe.idControleQuinquennal}/${axe.idAxe}`}
    >
      <Card
        className={classNames(style.itemWrapper, {
          [style.itemValide]:
            axe.status ===
            GatewayControleQuinquennal.ControleQuinquennalAxeStatus.VALIDE,
        })}
      >
        <h4>{axeTypeToLabel[axe.type]}</h4>
        <div>
          {axe.aiProcessingStatus && (
            <span className={style.aiStatus}>
              <IaSparkleIcon />
              {AI_PROCESSING_STATUS_LABELS[axe.aiProcessingStatus]}
            </span>
          )}
          <p className={style.fileNumber}>
            {axe.numberOfFiles}{" "}
            {axe.numberOfFiles > 1 ? "pièces déposées" : "pièce déposée"}
          </p>
          <Divider orientation="vertical" className={style.divider} />
          <span className={style.status}>
            <TagWithIcon variant={info.variant} overrideIcon={info.icon}>
              {info.label}
            </TagWithIcon>
          </span>
        </div>
      </Card>
    </Link>
  );
}

export type CQAxesPageContentProps = {
  idEntreprise: string;
};

export function CQAxesPageContent({ idEntreprise }: CQAxesPageContentProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: controleQuinquennal, isLoading } = useCQWithAxesQuery(
    idEntreprise,
    { refetchOnWindowFocus: "always" }
  );

  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: controleQuinquennalKeys.detail(idEntreprise),
    });
  }, [idEntreprise, queryClient]);
  const { mutate: validateCQ, isPending } = useValidateCQMutation(idEntreprise);
  const { mutate: launchAnalyse, isPending: isLaunchingAnalyse } =
    useLaunchAnalyseMutation(idEntreprise);
  const { openConfirmDialog } = useDialog();
  const { data: estimation } = useAnalyseEstimationQuery(
    controleQuinquennal?.id,
    { scope: "DOSSIER" }
  );

  const axesSorted = useMemo(
    () =>
      controleQuinquennal?.controleQuinquennalAxes
        ? [...controleQuinquennal.controleQuinquennalAxes].sort(
            (axe1, axe2) =>
              controleTypeOrder[axe1.type] - controleTypeOrder[axe2.type]
          )
        : [],
    [controleQuinquennal?.controleQuinquennalAxes]
  );

  const canSubmit = axesSorted.every(
    (axe) =>
      axe.status !==
      GatewayControleQuinquennal.ControleQuinquennalAxeStatus.EN_COURS
  );

  const totalFiles = useMemo(
    () => axesSorted.reduce((sum, axe) => sum + axe.numberOfFiles, 0),
    [axesSorted]
  );

  const { triggerExport, isExporting } = useDossierZipExport({
    controleQuinquennalId: controleQuinquennal?.id,
  });

  // Les métriques arrivent après le dossier : leur calcul passe par HubSpot
  // puis par tous les questionnaires du contrôle et de ses assujettis.
  const metriques = useCQMetriquesQuery(
    controleQuinquennal ? [controleQuinquennal] : []
  );

  if (isLoading || !controleQuinquennal)
    return (
      <ConnectedPageTemplate>
        <LoadingPage withText={false} />
      </ConnectedPageTemplate>
    );

  const onValidate = () => {
    const hasRemediatedAxe = axesSorted.some(
      (axe) =>
        axe.status ===
        GatewayControleQuinquennal.ControleQuinquennalAxeStatus.REMEDIE
    );

    if (!hasRemediatedAxe) {
      navigate(cqRapportUrl(idEntreprise, controleQuinquennal?.id));
      return;
    }

    try {
      openConfirmDialog({
        title: "Vous allez envoyer une demande de remédiation",
        message:
          "Un axe n’étant pas conforme, l'utilisateur contrôlé sera invité à compléter ou corriger sa conformité avant la fin de l'exercice.",
        cancelText: "Retour",
        confirmText: "Confirmer",
      })
        .then((shouldValidate) => {
          if (!shouldValidate) return;
          validateCQ({
            idCQ: controleQuinquennal.id,
            siren: idEntreprise,
          });
          navigate(`/`);
        })
        .catch(console.error);
    } catch (error) {
      console.error("Error during validation confirmation dialog", error);
    }
  };

  const handleLaunchAnalyse = () => {
    const costBlockedMessage = getAnalyseCostBlockedMessage(estimation);
    if (costBlockedMessage) {
      displayErrorToast(costBlockedMessage);
      return;
    }
    openConfirmDialog({
      title: "Lancer l'analyse IA du dossier",
      message: (
        <AnalyseEstimationMessage
          message="Lancer l'analyse IA de tous les axes de ce dossier ? Un seul lancement est possible par dépôt ; utilisez ensuite les relances par axe."
          estimation={estimation}
        />
      ),
      cancelText: "Annuler",
      confirmText: "Lancer",
    })
      .then((shouldLaunch) => {
        if (!shouldLaunch) return;
        launchAnalyse(
          { idControleQuinquennal: controleQuinquennal.id },
          {
            onSuccess: () => {
              displaySucessToast(
                "Analyse IA lancée. Le statut de chaque axe se met à jour automatiquement."
              );
            },
            onError: (error) => {
              if (isAnalyseCostBlockedError(error)) {
                displayErrorToast(ANALYSE_COST_BLOCKED_MESSAGE);
                return;
              }
              if (isAxiosError(error) && error.response?.status === 409) {
                displayErrorToast(
                  "L'analyse a déjà été lancée pour ce dépôt. Utilisez les relances par axe."
                );
                return;
              }
              displayErrorToast(
                "Impossible de lancer l'analyse IA du dossier."
              );
            },
          }
        );
      })
      .catch(() => {});
  };

  const isPreCommission =
    controleQuinquennal.pipelineStage ===
    GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus
      .PRE_COMMISSION;
  const attestationDejaDeposee =
    controleQuinquennal.status ===
      GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE &&
    controleQuinquennal.dateRapportFinal != null;

  const { entrepriseName } = controleQuinquennal;
  const { millesime } = controleQuinquennal;
  const formattedTitle = formatTitle(entrepriseName, millesime);

  const pipelineStageInfo =
    TICKET_STATUS_MAP[controleQuinquennal.pipelineStage];

  const axesTraites = axesSorted.filter(
    (axe) =>
      axe.status ===
      GatewayControleQuinquennal.ControleQuinquennalAxeStatus.VALIDE
  );
  const axesAControler = axesSorted.filter(
    (axe) =>
      axe.status !==
      GatewayControleQuinquennal.ControleQuinquennalAxeStatus.VALIDE
  );

  const formatDate = (date: Date | string | null) =>
    date ? format(new Date(date), "dd/MM/yyyy") : "-";
  const formatPourcentage = (pourcentage: number | null | undefined) =>
    pourcentage == null ? "-" : `${pourcentage}%`;
  const valeurs = metriques.parControle.get(controleQuinquennal.id);
  const enAttente = metriques.isPending(controleQuinquennal.id);
  const indicateur = (valeur: string) => (enAttente ? "…" : valeur);

  const indicateurs: {
    label: string;
    value: string;
    detail?: string;
  }[] = [
    {
      label: "Date de début",
      value: formatDate(
        controleQuinquennal.dateDebut ?? controleQuinquennal.dateCreation
      ),
    },
    {
      label: "Date de dépôt",
      value: formatDate(controleQuinquennal.dateDepot),
    },
    {
      label: "Date butoir",
      value: formatDate(getControleQuinquennalDateButoir(controleQuinquennal)),
    },
    {
      label: "Conformité IA",
      value: indicateur(formatPourcentage(valeurs?.conformiteIa)),
      detail:
        "Nombre de réponses valides d'après l'IA sur le nombre total de questions des questionnaires qu'elle a analysés.\n" +
        "Une réponse est valide quand elle n'est ni non conforme (rouge) ni manquante (orange) ; une réponse indéterminée n'est pas valide.",
    },
    {
      label: "Conformité vérificateur",
      value: indicateur(formatPourcentage(valeurs?.conformiteVerificateur)),
      detail:
        "Nombre de réponses valides sur le nombre total de questions posées du contrôle.\n" +
        "Une réponse est valide quand elle n'est ni non conforme (rouge) ni manquante (orange) ; une question sans réponse n'est pas valide.\n" +
        "Toutes les questions d'un axe validé comptent comme valides.",
    },
    {
      label: "Avancée du traitement",
      value: indicateur(
        !valeurs || valeurs.questionsTotal === 0
          ? "-"
          : `${valeurs.questionsRepondues}/${valeurs.questionsTotal}`
      ),
      detail:
        "Nombre de questions répondues sur le nombre total de questions posées.\n" +
        "Sont comptées les questions des questionnaires d'axe et celles des questionnaires de chaque personne assujettie.\n" +
        "Une question masquée par une condition d'affichage n'est pas posée, donc pas comptée ; le champ « Commentaire », facultatif, n'est pas une question.\n" +
        "Le statut de l'axe n'entre pas en compte : seules les réponses saisies ou pré-remplies sont comptées.\n" +
        "La première question d'un axe Garantie financière est pré-remplie d'après la déclaration de l'adhérent tant que le vérificateur n'y a pas répondu.",
    },
  ];

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    { label: formattedTitle, value: "current" },
  ];

  return (
    <PageWithTitleTemplate
      title=""
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          const currentRoute = breadcrumbElements.at(-1);
          if (currentRoute && nextRoute !== currentRoute.value) {
            navigate(nextRoute);
          }
        },
        selected: breadcrumbElements[1].value,
      }}
      actions={
        isControleQuinquennalReadOnly(
          controleQuinquennal.pipelineStage
        ) ? undefined : (
          <div className={style.actions}>
            {isRecette && (
              <Button
                variant="outline-accent"
                icon={<IaSparkleIcon />}
                iconVariant="right"
                disabled={
                  isLaunchingAnalyse || controleQuinquennal.analyseDossierLancee
                }
                onClick={handleLaunchAnalyse}
              >
                Lancer l&apos;analyse IA
              </Button>
            )}
            <Button
              icon={<CheckIcon />}
              iconVariant="right"
              disabled={!canSubmit || isPending}
              onClick={onValidate}
            >
              Soumettre le contrôle
            </Button>
            {isPreCommission && !attestationDejaDeposee && (
              <Button
                icon={<CheckIcon />}
                iconVariant="right"
                disabled={isPending}
                onClick={() =>
                  navigate(cqRapportUrl(idEntreprise, controleQuinquennal?.id))
                }
              >
                Déposer l&apos;attestation de fin de vérification
              </Button>
            )}
          </div>
        )
      }
    >
      <div className={style.header}>
        <TitleWithNumber title={formattedTitle} />
        <TagWithIcon
          className={style.headerTag}
          variant={pipelineStageInfo.variant}
          overrideIcon={pipelineStageInfo.icon}
        >
          {pipelineStageInfo.label}
        </TagWithIcon>
        <Button
          className={style.downloadButton}
          variant="text"
          icon={isExporting ? <Loader /> : <Download />}
          disabled={isExporting}
          onClick={() => {
            triggerExport().catch(() => {});
          }}
        >
          {`Télécharger toutes les pièces (${totalFiles})`}
        </Button>
      </div>
      <div className={style.indicateurs}>
        {indicateurs.map(({ label, value, detail }) => (
          <Card className={style.indicateur} key={label}>
            <div className={style.indicateurHeader}>
              <p className={style.indicateurLabel}>{label}</p>
              {detail && (
                <InfoTooltip ariaLabel={`Détail du calcul : ${label}`}>
                  {detail}
                </InfoTooltip>
              )}
            </div>
            <p className={style.indicateurValue}>{value}</p>
          </Card>
        ))}
      </div>
      <div className={style.main}>
        <div className={style.container}>
          {controleQuinquennal.pipelineStage ===
            GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus
              .PRE_COMMISSION && (
            <InfoCard type="warning" className={style.infoCardPreCommission}>
              Le contrôle quinquennal est à l&apos;état de pré-commission
            </InfoCard>
          )}
          {[
            { title: "Axes à contrôler", axes: axesAControler },
            { title: "Axes traités", axes: axesTraites },
          ]
            .filter((section) => section.axes.length > 0)
            .map((section) => (
              <div className={style.section} key={section.title}>
                <div className={style.sectionHeader}>
                  <h3>{section.title}</h3>
                  <p>
                    {section.axes.length} / {axesSorted.length}
                  </p>
                </div>
                <div className={style.axes}>
                  {section.axes.map((axe) => (
                    <ControleQuinquennalAxeItem
                      key={axe.idAxe}
                      axe={axe}
                      idEntreprise={idEntreprise}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </PageWithTitleTemplate>
  );
}

export function CQAxesPage() {
  const { idEntreprise } = useParams<CQAxesPageContentProps>();
  if (!idEntreprise) return <div>Impossible d&apos;afficher la page</div>;
  return <CQAxesPageContent idEntreprise={idEntreprise} />;
}
